import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useIntl, FormattedMessage } from 'react-intl';
import isAfter from 'date-fns/isAfter';
import parseISO from 'date-fns/parseISO';
import { Container, Flex, Loading, Alert } from '../../components';
import { pushRoute, replaceRoute } from '../../components/Link';
import ProductComingSoon from './ProductComingSoon';
import PhoneStep from './Step/PhoneStep';
import { RegistrationFeeStep } from './Step/RegistrationFeeStep';
import { ConfirmEmailStep } from './Step/ConfirmEmailStep';
import SummaryStep from './Step/SummaryStep';
import { useValue } from '../CheckoutValueContext';
import { Wizard, useWizardContext } from '../../components/Wizard';
import {
  productBySlugQuery,
  productBySlugQueryVariables,
  SanityImageOptionFit,
  SanityImageOptionCrop,
  PaymentDocumentType
} from '../../core-types';
import { PRODUCT_FRAGMENT } from '../../lib/core/fragments';
import { gql } from '@apollo/client';
import CurrentUserSubscription from './CurrentUserSubscription';
import { captureException } from '../../lib/sentry';
import { useCurrentUser } from '../../lib/core/useCurrentUser';
import { useSystem } from '../../lib/core/useSystem';
import { useRouter } from 'next/router';
import { getQueryString } from '../../lib/utils';
import { useAsyncProcessCompletion } from '../AsyncProcessCompletion';
import { setLocalStorageItems } from '../../components/SystemAlert';

const PRODUCT = gql`
  query productBySlugQuery(
    $slug: String!
    $productImageOptions: SanityImageOptions
  ) {
    productBySlug(slug: $slug) {
      ...Product
      partners {
        id
      }
    }
    system {
      id
      inSeason
      nextSeason {
        startDate
      }
      registrationFeeEnabled
      userEmailConfirmationRequired
    }
  }
  ${PRODUCT_FRAGMENT}
`;

const Checkout = ({
  productSlug,
  step,
  privacyPageSlug,
  urlQuery,
  listName
}: {
  productSlug: string;
  step?: string;
  privacyPageSlug: string | null;
  urlQuery: Record<string, any>;
  listName: string | null;
}) => {
  const globalSystem = useSystem();
  const { currentUser, loading, refetch } = useCurrentUser();
  const intl = useIntl();
  const router = useRouter();
  const [state, dispatch] = useValue();
  const stepIndex = step ? parseInt(step) : 0;
  const [emailStepShown, setEmailStepShown] = useState<boolean>(() => {
    const isEmailConfirmed = currentUser?.emailConfirmed;
    const hasConfirmedBefore =
      typeof window !== 'undefined'
        ? localStorage.getItem('emailConfirmationCompleted')
        : null;

    return !isEmailConfirmed && !hasConfirmedBefore;
  });

  useEffect(() => {
    if (listName && listName !== state.listName) {
      dispatch({ type: 'SET_LIST_NAME', payload: listName });
    }
  }, [listName, state.listName]);

  const productBySlugQueryResult = useQuery<
    productBySlugQuery,
    productBySlugQueryVariables
  >(PRODUCT, {
    variables: {
      slug: productSlug,
      productImageOptions: {
        width: 750,
        height: 750,
        fit: SanityImageOptionFit.crop,
        crop: SanityImageOptionCrop.entropy
      }
    }
  });

  const token = getQueryString(router.query.token);
  const paymentState = getQueryString(router.query.state);
  const type = getQueryString(router.query.type) as PaymentDocumentType;

  const asyncProcessCompletionResult = useAsyncProcessCompletion({
    token,
    paymentState: 'success',
    skip: !token,
    type,
    onAsyncProcessCompleted: state => {
      switch (state) {
        case 'success': {
          replaceRoute({
            intl,
            route: 'post-checkout',
            params: {
              token,
              type,
              locale: intl.locale
            }
          });
          // refetch currentUser
          refetch();
          break;
        }
        case 'cancelled': {
          // do nothing
          break;
        }
        case 'failed':
        case 'error': {
          // error is shown in the UI
          break;
        }
      }
    }
  });

  if (
    !productBySlugQueryResult ||
    !productBySlugQueryResult.data ||
    !productBySlugQueryResult.data.productBySlug
  ) {
    return null;
  }
  const product = productBySlugQueryResult.data.productBySlug;
  const system = productBySlugQueryResult.data.system;
  const { paymentProvider } = globalSystem;

  if (!productBySlugQueryResult.loading && !product) {
    const err = new Error('Product not found');
    err.code = 'ENOENT';
    throw err;
  }

  if (!system) {
    const err = new Error('System not found');
    err.code = 'ENOENT';
    throw err;
  }

  if (!paymentProvider) {
    throw new Error('PaymentProvider not setup');
  }

  if (product.purchasableFrom) {
    const now = new Date();
    const purchasableFrom = parseISO(product.purchasableFrom);
    if (isAfter(purchasableFrom, now)) {
      return (
        <Container pt={[0, 0, 0, 7]}>
          <ProductComingSoon product={product} system={system} />
        </Container>
      );
    }
  }

  if (token && paymentState === 'success') {
    const asyncProcessCompletionLoading =
      asyncProcessCompletionResult.loading ||
      asyncProcessCompletionResult.postMessageState === 'success';
    if (asyncProcessCompletionLoading) {
      return (
        <Flex alignItems="center" justifyContent="center" height="100vh">
          <Loading />
          <FormattedMessage
            id="paymentProcessing"
            defaultMessage="Processing payment…"
          />
        </Flex>
      );
    }

    if (
      asyncProcessCompletionResult.postMessageState === 'error' ||
      asyncProcessCompletionResult.postMessageState === 'failed'
    ) {
      return (
        <Flex alignItems="center" justifyContent="center" height="100vh">
          <Alert variant="error">
            <FormattedMessage
              id="paymentError"
              defaultMessage="Could not process payment."
            />
          </Alert>
        </Flex>
      );
    }
  }

  if (currentUser && currentUser.currentSubscription) {
    return (
      <Container pt={[0, 0, 0, 7]}>
        <CurrentUserSubscription />
      </Container>
    );
  }

  if (!step) {
    return (
      <Container pt={[0, 0, 0, 7]}>
        <PhoneStep
          product={product}
          privacyPageSlug={privacyPageSlug}
          step={step}
          intl={intl}
          productSlug={productSlug}
          currentUser={currentUser}
          system={system}
        />
      </Container>
    );
  }

  const steps: (() => JSX.Element)[] = [];

  if (emailStepShown && system.userEmailConfirmationRequired) {
    steps.push(function ConfirmEmailStepFunc() {
      const { goNext } = useWizardContext();
      return (
        <ConfirmEmailStep
          goNext={() => {
            setLocalStorageItems('emailConfirmationCompleted', 'true');
            setEmailStepShown(true);
            goNext();
          }}
        />
      );
    });
  }

  system.registrationFeeEnabled &&
    steps.push(function RegistrationFeeStepFunc() {
      const { goNext } = useWizardContext();
      return <RegistrationFeeStep goNext={goNext} />;
    });

  steps.push(function SummaryStepFunc() {
    return (
      <SummaryStep
        product={product}
        urlQuery={urlQuery}
        currentUser={currentUser}
        currentUserLoading={loading}
        productSlug={productSlug}
        paymentProvider={paymentProvider}
        availablePaymentMethods={globalSystem.availablePaymentMethods}
      />
    );
  });

  const StepComponent = steps[stepIndex];

  return (
    <Flex
      alignItems="stretch"
      justifyContent="center"
      flexDirection="column"
      minHeight="100vh"
      position="relative"
      style={{ overflow: 'hidden' }}
    >
      <Wizard
        stepIndex={stepIndex}
        stepCount={steps.length}
        showBackOnFirst
        onStepChange={props => {
          if (props.finished) {
            pushRoute({
              route: 'post-checkout',
              intl
            });
          } else if (props.nextStepIndex !== null) {
            const nextStep = steps[props.nextStepIndex];
            if (!nextStep) {
              captureException(new Error('Next step not found'), {});
              return;
            }
            if (stepIndex === props.nextStepIndex) {
              return;
            }
            pushRoute({
              route: 'buy',
              params: {
                productSlug,
                step: props.nextStepIndex
              },
              options: {
                shallow: true
              },
              intl
            });
          }
        }}
      >
        <StepComponent />
      </Wizard>
    </Flex>
  );
};

export default Checkout;
